import { Button, Spinner } from '@monksflow/monks-ui';
import { RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { VideoPlayer } from '@/shared/ui/VideoPlayer';
import type { VideoCardProps } from './VideoCard.types';

const FILL_CENTERED = 'flex size-full items-center justify-center bg-muted';

export function VideoCard(props: VideoCardProps) {
  const { t } = useTranslation('audiences');

  const { label, videoAssetId, metadata, hasError, errorMessage, isRetrying, onRetry } = props;

  const { downloadUrl, isLoading } = useGetDownloadUrl(videoAssetId ?? undefined);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs">{label}</p>

      <div className="aspect-video overflow-hidden rounded-2xl border border-border">
        {isRetrying ? (
          <div className={FILL_CENTERED}>
            <Spinner size="lg" />
          </div>
        ) : hasError ? (
          <div className="flex size-full flex-col overflow-y-auto bg-muted">
            {/*
              Centred by auto margins rather than justify-content: a reason long
              enough to overflow this 16:9 box would sit partly above the scroll
              origin and be unreachable, whereas auto margins collapse to zero
              once the content stops fitting.
            */}
            <div className="m-auto flex flex-col items-center gap-3 p-4">
              <p className="text-center text-xs text-muted-foreground">
                {errorMessage ?? t('audiences.videoCard.couldnTGenerateThisVideo')}
              </p>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  <RefreshCwIcon />
                  {t('audiences.videoCard.tryAgain')}
                </Button>
              )}
            </div>
          </div>
        ) : isLoading ? (
          <div className={FILL_CENTERED}>
            <Spinner size="lg" />
          </div>
        ) : downloadUrl ? (
          <VideoPlayer videoSrc={downloadUrl} className="scale-[1.02]" />
        ) : (
          <div className={FILL_CENTERED}>
            <p className="text-xs text-muted-foreground">{t('audiences.videoCard.noVideo')}</p>
          </div>
        )}
      </div>

      {metadata && (
        <div className="flex flex-col gap-1">
          {metadata.createdAt && (
            <p className="text-xs text-muted-foreground">
              {t('audiences.videoCard.created', { createdAt: metadata.createdAt })}
            </p>
          )}
          {metadata.promptUsed && metadata.videoType === 'detail' && (
            <div className="flex flex-col gap-0.5">
              <p className="text-xs text-muted-foreground">{t('audiences.videoCard.promptUsed')}</p>
              <p className="text-xs">{metadata.promptUsed}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
